import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import { useFormateurs, useStatsFormations } from '@/hooks/queries/useFormation';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import { formatDA, formatDACompact } from '@/lib/format';

export function RapportsPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);

  const { data: rawStats = [] } = useStatsFormations(eurlKostGroup?.id ?? null);
  const stats = useMemo(
    () =>
      segmentIds.length > 0
        ? rawStats.filter((s) => s.sous_segment_id && segmentIds.includes(s.sous_segment_id))
        : rawStats,
    [rawStats, segmentIds],
  );
  const { data: formateurs = [] } = useFormateurs();
  const { data: sousSegments = [] } = useAllSousSegments();

  const ssMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sousSegments) m.set(s.id, s.nom);
    return m;
  }, [sousSegments]);

  // CA par sous-segment (graphique)
  const caBySS = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of stats) {
      const key = s.sous_segment_id ? (ssMap.get(s.sous_segment_id) ?? 'Autre') : 'Non typé';
      m.set(key, (m.get(key) ?? 0) + Number(s.ca_realise));
    }
    return Array.from(m.entries()).map(([nom, ca]) => ({ nom, ca }));
  }, [stats, ssMap]);

  const top5Formations = useMemo(() => {
    return stats
      .slice()
      .sort((a, b) => Number(b.ca_realise) - Number(a.ca_realise))
      .slice(0, 5);
  }, [stats]);

  const top5Formateurs = useMemo(() => {
    return formateurs
      .filter((f) => f.note_moyenne != null)
      .slice()
      .sort((a, b) => Number(b.note_moyenne ?? 0) - Number(a.note_moyenne ?? 0))
      .slice(0, 5);
  }, [formateurs]);

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Rapports & statistiques"
        subtitle="Performance catalogue, formateurs et CA agrégé."
        actions={<SelecteurSousSegmentFormation value={segment} onChange={setSegment} />}
      />

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">CA réalisé par sous-segment</h3>
        <div className="h-64 w-full">
          {caBySS.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Aucune donnée.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={caBySS}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="nom" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v: number) => formatDACompact(v)} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v: number) => formatDA(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ca" name="CA réalisé" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Top 5 formations par CA</h3>
          <ol className="space-y-1.5 text-xs">
            {top5Formations.map((s, i) => (
              <li
                key={s.formation_id}
                className="flex items-center justify-between border-b border-border/40 py-1"
              >
                <span className="line-clamp-1">
                  <span className="mr-1 font-mono text-muted-foreground">#{i + 1}</span>
                  {s.titre}
                </span>
                <div className="flex items-center gap-3">
                  <span className="num">{formatDA(Number(s.ca_realise))}</span>
                  {s.taux_remplissage_pct != null && (
                    <Badge variant="outline" className="text-[10px]">
                      {Number(s.taux_remplissage_pct)}%
                    </Badge>
                  )}
                </div>
              </li>
            ))}
            {top5Formations.length === 0 && (
              <li className="text-muted-foreground">Aucune donnée.</li>
            )}
          </ol>
        </section>

        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Top 5 formateurs par note moyenne</h3>
          <ol className="space-y-1.5 text-xs">
            {top5Formateurs.map((f, i) => (
              <li
                key={f.id}
                className="flex items-center justify-between border-b border-border/40 py-1"
              >
                <span>
                  <span className="mr-1 font-mono text-muted-foreground">#{i + 1}</span>
                  {f.prenom} {f.nom}
                </span>
                <Badge variant="success" className="text-[10px]">
                  {Number(f.note_moyenne ?? 0).toFixed(2)} / 5
                </Badge>
              </li>
            ))}
            {top5Formateurs.length === 0 && <li className="text-muted-foreground">Aucune note.</li>}
          </ol>
        </section>
      </div>

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Performance détaillée par formation</h3>
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="py-2">Formation</th>
                <th className="py-2 text-right">Sessions</th>
                <th className="py-2 text-right">Inscriptions</th>
                <th className="py-2 text-right">Taux rempl.</th>
                <th className="py-2 text-right">CA réalisé</th>
                <th className="py-2 text-right">CA engagé</th>
                <th className="py-2 text-right">NPS</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.formation_id} className="border-b border-border/40">
                  <td className="line-clamp-1 py-1">{s.titre}</td>
                  <td className="num py-1 text-right">{s.nb_sessions}</td>
                  <td className="num py-1 text-right">{s.nb_inscriptions_actives}</td>
                  <td className="num py-1 text-right">
                    {s.taux_remplissage_pct != null ? `${Number(s.taux_remplissage_pct)}%` : '—'}
                  </td>
                  <td className="num py-1 text-right">{formatDA(Number(s.ca_realise))}</td>
                  <td className="num py-1 text-right">{formatDA(Number(s.ca_engage))}</td>
                  <td className="num py-1 text-right">
                    {s.nps_moyen != null ? Number(s.nps_moyen).toFixed(1) : '—'}
                  </td>
                </tr>
              ))}
              {stats.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">
                    Aucune donnée.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
